/**
 * PRD.305 — presentation pieces shared by every laboratory surface.
 *
 * The Laboratory module, Patient Workspace Labs and Consultation Labs all read
 * the same canonical rows, so they also share the same status vocabulary and
 * badges. Keeping these here is what stops "Result Ready" from looking like one
 * thing in the module and another thing in the chart.
 *
 * PRD §17 requires four states to be visually distinguishable: still
 * processing, uploaded-but-not-published, published, and cancelled. The badge
 * palette below is the single place that distinction is expressed.
 */
import type { CSSProperties, ReactNode } from 'react';
import {
  LAB_TEST_STATUS_LABELS,
  normalizeLabTestStatus,
  type LabTestStatus,
} from '@helios/data-access';

const STATUS_COLORS: Record<LabTestStatus, { bg: string; fg: string }> = {
  ordered: { bg: '#f1f5f9', fg: '#475569' },
  collected: { bg: '#e0f2fe', fg: '#075985' },
  in_progress: { bg: '#fef3c7', fg: '#92400e' },
  // Amber-to-violet shift: "ready but not yet clinical" must not read as done.
  result_ready: { bg: '#ede9fe', fg: '#5b21b6' },
  published: { bg: '#dcfce7', fg: '#166534' },
  cancelled: { bg: '#fee2e2', fg: '#991b1b' },
};

const badgeBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 22,
  padding: '0 var(--sp-2)',
  borderRadius: 'var(--radius-xs)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
  whiteSpace: 'nowrap',
};

/** Status chip for a test, or for an order's derived aggregate status. */
export function LabStatusBadge({
  status,
  prefix,
}: {
  status: string | null | undefined;
  /** e.g. "Order" so an aggregate reads "Order · In Progress". */
  prefix?: string;
}) {
  const normalized = normalizeLabTestStatus(status);
  const colors = STATUS_COLORS[normalized];
  return (
    <span style={{ ...badgeBase, background: colors.bg, color: colors.fg }}>
      {prefix ? `${prefix} · ` : ''}
      {LAB_TEST_STATUS_LABELS[normalized]}
    </span>
  );
}

/**
 * Settlement chip. Billing stays authoritative — Laboratory only displays what
 * it finds, and never gates work on it (PRD §11).
 */
export function LabPaymentBadge({ status }: { status: string | null | undefined }) {
  if (!status) {
    return (
      <span style={{ ...badgeBase, background: '#f1f5f9', color: '#64748b' }}>Not billed</span>
    );
  }
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    paid: { bg: '#dcfce7', fg: '#166534', label: 'Paid' },
    partial: { bg: '#fef3c7', fg: '#92400e', label: 'Partly paid' },
    unpaid: { bg: '#fee2e2', fg: '#991b1b', label: 'Unpaid' },
  };
  const tone = map[status] ?? { bg: '#f1f5f9', fg: '#64748b', label: status };
  return <span style={{ ...badgeBase, background: tone.bg, color: tone.fg }}>{tone.label}</span>;
}

/** "Awaiting publication" marker — the visual counterpart to Published. */
export function LabUnpublishedBadge() {
  return (
    <span style={{ ...badgeBase, background: '#fff7ed', color: '#9a3412' }}>
      Uploaded · not published
    </span>
  );
}

export function LabEmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-8) var(--sp-6)',
        textAlign: 'center',
        color: 'var(--text-muted)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', maxWidth: 420, lineHeight: 1.5 }}>
        {description}
      </div>
      {action && <div style={{ marginTop: 'var(--sp-3)' }}>{action}</div>}
    </div>
  );
}

/** Parse the comma-joined `test_statuses` rollup into aggregation input. */
export function parseTestStatuses(joined: string | null | undefined): { status: string }[] {
  if (!joined) return [];
  return joined
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((status) => ({ status }));
}

/** Short, locale-stable date for list rows. */
export function formatLabDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}
